import React, { useEffect, useMemo, useState } from "react";
import {
  applyScheduleTemplateToProject,
  getScheduleTemplateStats,
  getScheduleTemplates,
} from "../../../../api/scheduleTemplatesApi";
import "../AddWorkTypeModal/AddWorkTypeModal.css";
import "./SelectTemplateModal.css";

const SelectTemplateModal = ({
  open,
  projectId,
  projectSections = [],
  buildingType,
  onClose,
  onApplied,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelectedId(null);
    setApplying(false);
    setApplyError("");
    setResult(null);
    setLoadError("");

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getScheduleTemplates();
        if (!cancelled) setTemplates(data);
      } catch (error) {
        if (!cancelled) {
          setLoadError(error?.message || "Не удалось загрузить шаблоны");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !applying) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, applying, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((tpl) => {
      const text =
        `${tpl.name || ""} ${tpl.description || ""} ${tpl.building_type || ""}`.toLowerCase();
      const matchesQuery = !q || text.includes(q);
      const matchesType =
        !buildingType ||
        !tpl.building_type ||
        tpl.building_type === buildingType;
      return matchesQuery && matchesType;
    });
  }, [templates, query, buildingType]);

  const selected =
    templates.find((tpl) => String(tpl.id) === String(selectedId)) || null;

  if (!open) return null;

  const handleApply = async () => {
    if (!selected || applying) return;
    try {
      setApplying(true);
      setApplyError("");
      const applyResult = await applyScheduleTemplateToProject(
        projectId,
        selected,
        projectSections,
      );
      setResult(applyResult);
      onApplied?.(applyResult);
    } catch (error) {
      setApplyError(error?.message || "Не удалось применить шаблон");
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <div
        className="add-work-type-modal-backdrop"
        onClick={() => !applying && onClose()}
      />
      <div
        className="add-work-type-modal pst-select-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="select-template-modal-title"
      >
        <h2
          id="select-template-modal-title"
          className="add-work-type-modal__title"
        >
          {result ? "Шаблон применён" : "Выбор шаблона графика"}
        </h2>

        {result ? (
          <div className="pst-select-result">
            <p className="pst-select-hint">
              Шаблон добавлен в совпадающие разделы. Остальные разделы и
              отдельные работы можно править вручную, сроки проставляются на
              проекте.
            </p>
            {result.message && (
              <p className="pst-select-hint">{result.message}</p>
            )}
            {result.applied.length > 0 && (
              <div className="pst-select-result__block">
                <strong>Добавлено</strong>
                {result.applied.map((row) => (
                  <p key={row.sectionName}>
                    {row.sectionName}: {row.added.join(", ")}
                  </p>
                ))}
              </div>
            )}
            {result.relationsUpdated > 0 && (
              <div className="pst-select-result__block">
                <strong>Связи и порядок</strong>
                <p>
                  Создано связей: {result.relationsUpdated}
                </p>
              </div>
            )}
            {result.unmatched.length > 0 && (
              <div className="pst-select-result__block">
                <strong>Нет такого раздела в проекте</strong>
                {result.unmatched.map((row) => (
                  <p key={row.sectionName}>{row.sectionName}</p>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="pst-select-result__block">
                <strong>Пропущено</strong>
                {result.skipped.map((row) => (
                  <p key={`${row.sectionName}-${row.reason}`}>
                    {row.sectionName}: {row.reason}
                  </p>
                ))}
              </div>
            )}
            {result.errors.length > 0 && (
              <div className="pst-select-result__block pst-select-result__block--error">
                <strong>Ошибки</strong>
                {result.errors.map((row, index) => (
                  <p key={`${row.sectionName}-${row.name}-${index}`}>
                    {row.sectionName}
                    {row.name ? ` / ${row.name}` : ""}: {row.message}
                  </p>
                ))}
              </div>
            )}
            {result.applied.length === 0 &&
              result.errors.length === 0 &&
              result.skipped.length === 0 &&
              !result.relationsUpdated && (
                <p className="pst-select-hint">
                  Нечего добавлять — либо разделы не совпали, либо позиции уже
                  есть.
                </p>
              )}
            <div className="add-work-type-modal__actions">
              <button
                type="button"
                className="add-work-type-modal__btn add-work-type-modal__btn--submit"
                onClick={onClose}
              >
                Закрыть
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="pst-select-hint">
              В шаблоне задаются виды работ, вехи, удельный вес, порядок и связи.
              Сроки пользователь проставляет уже на проекте. Работы «вне разделов»
              не входят в % завершения РП, но могут быть связаны с разделами.
              После применения шаблон можно дополнять вручную.
            </p>

            <input
              type="search"
              className="pst-select-search"
              placeholder="Поиск по названию шаблона..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            {loading ? (
              <div className="pst-select-state">Загрузка шаблонов...</div>
            ) : loadError ? (
              <div className="pst-select-state pst-select-state--error">
                {loadError}
              </div>
            ) : filtered.length === 0 ? (
              <div className="pst-select-state">
                Шаблонов пока нет. Их может создать пользователь с доступом АДМС.
              </div>
            ) : (
              <div className="pst-select-list">
                {filtered.map((tpl) => {
                  const stats = getScheduleTemplateStats(tpl);
                  const isActive = String(tpl.id) === String(selectedId);
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      className={`pst-select-card${
                        isActive ? " pst-select-card--active" : ""
                      }`}
                      onClick={() => {
                        setSelectedId(tpl.id);
                        setApplyError("");
                      }}
                    >
                      <div className="pst-select-card__title">{tpl.name}</div>
                      {tpl.description && (
                        <div className="pst-select-card__desc">
                          {tpl.description}
                        </div>
                      )}
                      <div className="pst-select-card__meta">
                        {tpl.building_type && <span>{tpl.building_type}</span>}
                        <span>{stats.sectionsCount} разд.</span>
                        {stats.milestonesCount == null ? (
                          <span>{stats.itemsCount} поз.</span>
                        ) : (
                          <>
                            <span>{stats.workTypesCount} работ</span>
                            <span>{stats.milestonesCount} вех</span>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {applyError && (
              <p className="add-work-type-modal__error">{applyError}</p>
            )}

            <div className="add-work-type-modal__actions">
              <button
                type="button"
                className="add-work-type-modal__btn add-work-type-modal__btn--cancel"
                onClick={onClose}
                disabled={applying}
              >
                Отмена
              </button>
              <button
                type="button"
                className="add-work-type-modal__btn add-work-type-modal__btn--submit"
                onClick={handleApply}
                disabled={!selected || applying}
              >
                {applying ? "Применение..." : "Применить шаблон"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SelectTemplateModal;
